import { FC, useEffect, useMemo, useState } from 'react';
import { useForm, FormProvider } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z, ZodType } from 'zod';
import { Grid, Typography, Divider } from '@mui/material';
import StructuredModal from 'src/views/components/structuredModal/structuredModal';
import ControlledFileUploadField from 'src/views/components/controlledComponents/ControlledFileUploadField.component';
import ControlledSelectField from 'src/views/components/controlledComponents/ControlledSelectField.component';
import ControlledTextField from 'src/views/components/controlledComponents/ControlledTextField.component';
import ControlledDateField from 'src/views/components/controlledComponents/ControlledDateField.component';
import {
  IStaffPayout,
  PAYOUT_STATUS,
  PAYMENT_METHOD,
  PAY_PERIOD,
  ISalaryBreakdown,
  IBankDetails,
  ITaxDetails,
} from 'src/apis/staff/staffPayout.interfaces';
import {
  useStaffPayoutActions,
  useStaffPayoutState,
} from '../../contexts/staffPayout.context';
import { useTranslation } from 'src/hooks/useTranslationHook';
import { TKeyValue } from 'src/utils/commonInterfaces';
import { TPrimaryDetails } from 'src/apis/users/users.interfaces';
import { TDocument } from 'src/apis/documents/documents.interfaces';
import { useStaffListHook } from '../../hooks/useStaffListHook';
import { TDocumentStatus } from 'src/apis/documents/documents.interfaces';
import {
  zNumberFromInput,
  zOptionalDateStringFromInput,
  zOptionalNumberFromInput,
  zYearFromInput,
} from 'src/utils/zodFormHelpers';

type StaffPayoutFormData = {
  userId: string;
  salaryBreakdown: ISalaryBreakdown;
  month: string;
  year: number;
  financialYear: string;
  payPeriod: PAY_PERIOD;
  workingDays?: number;
  paidDays?: number;
  status: PAYOUT_STATUS;
  paymentMethod?: PAYMENT_METHOD;
  paymentDate?: string;
  transactionReference?: string;
  bankDetails?: IBankDetails;
  taxDetails?: ITaxDetails;
  payslip: {
    documentName: string;
    description?: string;
    verificationDate?: string;
    status: TDocumentStatus;
  };
  remarks?: string;
};

const getStaffPayoutFormSchema = (
  translateText: (key: string) => string
): ZodType<StaffPayoutFormData> =>
  z.object({
    userId: z.string().min(1, translateText('common.fieldRequired')),
    salaryBreakdown: z.object({
      basic: zNumberFromInput({
        min: 0,
        message: translateText('common.fieldRequired'),
      }),
      hra: zOptionalNumberFromInput(),
      da: zOptionalNumberFromInput(),
      transportAllowance: zOptionalNumberFromInput(),
      medicalAllowance: zOptionalNumberFromInput(),
      specialAllowance: zOptionalNumberFromInput(),
      otherAllowances: zOptionalNumberFromInput(),
      grossSalary: zNumberFromInput({ min: 0 }),
      providentFund: zOptionalNumberFromInput(),
      employeeStateInsurance: zOptionalNumberFromInput(),
      professionalTax: zOptionalNumberFromInput(),
      incomeTax: zOptionalNumberFromInput(),
      otherDeductions: zOptionalNumberFromInput(),
      totalDeductions: zNumberFromInput({ min: 0 }),
      netSalary: zNumberFromInput({ min: 0 }),
    }),
    month: z.string().min(1, translateText('common.fieldRequired')),
    year: zYearFromInput({
      min: 2000,
      max: 2100,
      message: translateText('common.fieldRequired'),
    }),
    financialYear: z.string().min(1, translateText('common.fieldRequired')),
    payPeriod: z.nativeEnum(PAY_PERIOD, {
      required_error: translateText('common.fieldRequired'),
    }),
    workingDays: zOptionalNumberFromInput(),
    paidDays: zOptionalNumberFromInput(),
    status: z.nativeEnum(PAYOUT_STATUS, {
      required_error: translateText('common.fieldRequired'),
    }),
    paymentMethod: z.nativeEnum(PAYMENT_METHOD).optional(),
    paymentDate: zOptionalDateStringFromInput({
      message: translateText('common.dateInvalid') || undefined,
    }),
    transactionReference: z.string().optional(),
    bankDetails: z
      .object({
        accountNumber: z.string(),
        ifscCode: z.string(),
        bankName: z.string(),
        branchName: z.string().optional(),
      })
      .optional(),
    taxDetails: z
      .object({
        pan: z.string().optional(),
        uan: z.string().optional(),
        tdsAmount: zOptionalNumberFromInput(),
        taxSlab: z.string().optional(),
      })
      .optional(),
    payslip: z.object({
      documentName: z.string().min(1, translateText('common.fieldRequired')),
      description: z.string().optional(),
      verificationDate: zOptionalDateStringFromInput({
        message: translateText('common.dateInvalid') || undefined,
      }),
      status: z.nativeEnum(TDocumentStatus, {
        required_error: translateText('common.fieldRequired'),
      }),
    }),
    remarks: z.string().optional(),
  }) as ZodType<StaffPayoutFormData>;

const getStatusOptions = (
  translateText: (key: string) => string
): TKeyValue<string>[] => [
    {
      key: PAYOUT_STATUS.PAID,
      value: translateText('staff.payouts.status.paid') || 'Paid',
    },
    {
      key: PAYOUT_STATUS.PENDING,
      value: translateText('staff.payouts.status.pending') || 'Pending',
    },
    {
      key: PAYOUT_STATUS.PROCESSING,
      value: translateText('staff.payouts.status.processing') || 'Processing',
    },
    {
      key: PAYOUT_STATUS.FAILED,
      value: translateText('staff.payouts.status.failed') || 'Failed',
    },
  ];

const getPaymentMethodOptions = (
  translateText: (key: string) => string
): TKeyValue<string>[] => [
    {
      key: PAYMENT_METHOD.NEFT,
      value: translateText('staff.payouts.paymentMethod.neft') || 'NEFT',
    },
    {
      key: PAYMENT_METHOD.RTGS,
      value: translateText('staff.payouts.paymentMethod.rtgs') || 'RTGS',
    },
    {
      key: PAYMENT_METHOD.IMPS,
      value: translateText('staff.payouts.paymentMethod.imps') || 'IMPS',
    },
    {
      key: PAYMENT_METHOD.UPI,
      value: translateText('staff.payouts.paymentMethod.upi') || 'UPI',
    },
    {
      key: PAYMENT_METHOD.CHEQUE,
      value: translateText('staff.payouts.paymentMethod.cheque') || 'Cheque',
    },
    {
      key: PAYMENT_METHOD.CASH,
      value: translateText('staff.payouts.paymentMethod.cash') || 'Cash',
    },
  ];

const getPayPeriodOptions = (
  translateText: (key: string) => string
): TKeyValue<string>[] => [
    {
      key: PAY_PERIOD.FULL_MONTH,
      value: translateText('staff.payouts.payPeriod.fullMonth') || 'Full Month',
    },
    {
      key: PAY_PERIOD.PARTIAL,
      value: translateText('staff.payouts.payPeriod.partial') || 'Partial',
    },
    {
      key: PAY_PERIOD.ADVANCE,
      value: translateText('staff.payouts.payPeriod.advance') || 'Advance',
    },
  ];

const getMonthOptions = (): TKeyValue<string>[] => [
  { key: 'January', value: 'January' },
  { key: 'February', value: 'February' },
  { key: 'March', value: 'March' },
  { key: 'April', value: 'April' },
  { key: 'May', value: 'May' },
  { key: 'June', value: 'June' },
  { key: 'July', value: 'July' },
  { key: 'August', value: 'August' },
  { key: 'September', value: 'September' },
  { key: 'October', value: 'October' },
  { key: 'November', value: 'November' },
  { key: 'December', value: 'December' },
];

const getStatusOptionsForDocument = (
  translateText: (key: string) => string
): TKeyValue<string>[] => [
    {
      key: TDocumentStatus.SUBMITTED,
      value: translateText('documents.submitted') || 'Submitted',
    },
    {
      key: TDocumentStatus.PENDING,
      value: translateText('documents.pending') || 'Pending',
    },
    {
      key: TDocumentStatus.VERIFIED,
      value: translateText('documents.verified') || 'Verified',
    },
    {
      key: TDocumentStatus.REJECTED,
      value: translateText('documents.rejected') || 'Rejected',
    },
  ];

const AddEditStaffPayoutModal: FC = () => {
  const {
    staffPayoutAppState: {
      isModalOpen,
      selectedPayoutForEdit,
      mutationErrorMessage,
    },
  } = useStaffPayoutState();
  const { closeModal } = useStaffPayoutActions();
  const { translateText } = useTranslation();

  const { data: staffListResponse } = useStaffListHook({
    page: 1,
    limit: 1000,
  });

  const staffOptions = useMemo<TKeyValue<string>[]>(() => {
    if (!staffListResponse?.data) return [];
    return staffListResponse.data.map((staff) => ({
      key: staff.id,
      value:
        `${staff.basicDetails.firstName} ${staff.basicDetails.middleName || ''} ${staff.basicDetails.lastName}`.trim(),
    }));
  }, [staffListResponse]);

  const documentFormSchema = getStaffPayoutFormSchema(translateText);

  const defaultValues: StaffPayoutFormData = {
    userId: '',
    salaryBreakdown: {
      basic: 0,
      hra: 0,
      da: 0,
      transportAllowance: 0,
      medicalAllowance: 0,
      specialAllowance: 0,
      otherAllowances: 0,
      grossSalary: 0,
      providentFund: 0,
      employeeStateInsurance: 0,
      professionalTax: 0,
      incomeTax: 0,
      otherDeductions: 0,
      totalDeductions: 0,
      netSalary: 0,
    },
    month: '',
    year: new Date().getFullYear(),
    financialYear: '',
    payPeriod: PAY_PERIOD.FULL_MONTH,
    workingDays: undefined,
    paidDays: undefined,
    status: PAYOUT_STATUS.PENDING,
    paymentMethod: undefined,
    paymentDate: undefined,
    transactionReference: undefined,
    bankDetails: undefined,
    taxDetails: undefined,
    payslip: {
      documentName: '',
      description: '',
      verificationDate: '',
      status: TDocumentStatus.PENDING,
    },
    remarks: '',
  };

  const methods = useForm<StaffPayoutFormData>({
    resolver: zodResolver(documentFormSchema),
    mode: 'onBlur',
    defaultValues,
  });

  const {
    control,
    handleSubmit,
    formState: { errors },
    reset,
    watch,
    setValue,
  } = methods;

  const salaryBreakdown = watch('salaryBreakdown');

  const [payslipUploadProgress, setPayslipUploadProgress] = useState(false);
  const [payslipUploadProgressMessage, setPayslipUploadProgressMessage] =
    useState('');

  const onPayslipUploadProgress = (isUploading: boolean, message: string) => {
    setPayslipUploadProgress(isUploading);
    setPayslipUploadProgressMessage(message);
  };

  useEffect(() => {
    if (salaryBreakdown) {
      const earnings =
        (salaryBreakdown.basic || 0) +
        (salaryBreakdown.hra || 0) +
        (salaryBreakdown.da || 0) +
        (salaryBreakdown.transportAllowance || 0) +
        (salaryBreakdown.medicalAllowance || 0) +
        (salaryBreakdown.specialAllowance || 0) +
        (salaryBreakdown.otherAllowances || 0);

      const deductions =
        (salaryBreakdown.providentFund || 0) +
        (salaryBreakdown.employeeStateInsurance || 0) +
        (salaryBreakdown.professionalTax || 0) +
        (salaryBreakdown.incomeTax || 0) +
        (salaryBreakdown.otherDeductions || 0);

      const netSalary = earnings - deductions;

      // Update calculated fields
      setValue('salaryBreakdown.grossSalary', earnings, {
        shouldValidate: false,
      });
      setValue('salaryBreakdown.totalDeductions', deductions, {
        shouldValidate: false,
      });
      setValue('salaryBreakdown.netSalary', netSalary, {
        shouldValidate: false,
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    salaryBreakdown?.basic,
    salaryBreakdown?.hra,
    salaryBreakdown?.da,
    salaryBreakdown?.transportAllowance,
    salaryBreakdown?.medicalAllowance,
    salaryBreakdown?.specialAllowance,
    salaryBreakdown?.otherAllowances,
    salaryBreakdown?.providentFund,
    salaryBreakdown?.employeeStateInsurance,
    salaryBreakdown?.professionalTax,
    salaryBreakdown?.incomeTax,
    salaryBreakdown?.otherDeductions,
    setValue,
  ]);

  useEffect(() => {
    if (selectedPayoutForEdit && isModalOpen) {
      const user = selectedPayoutForEdit.user;
      reset({
        userId: user.id || '',
        salaryBreakdown: selectedPayoutForEdit.salaryBreakdown,
        month: selectedPayoutForEdit.month || '',
        year: selectedPayoutForEdit.year || new Date().getFullYear(),
        financialYear: selectedPayoutForEdit.financialYear || '',
        payPeriod: selectedPayoutForEdit.payPeriod || PAY_PERIOD.FULL_MONTH,
        workingDays: selectedPayoutForEdit.workingDays,
        paidDays: selectedPayoutForEdit.paidDays,
        status: selectedPayoutForEdit.status || PAYOUT_STATUS.PENDING,
        paymentMethod: selectedPayoutForEdit.paymentMethod,
        paymentDate: selectedPayoutForEdit.paymentDate,
        transactionReference: selectedPayoutForEdit.transactionReference,
        bankDetails: selectedPayoutForEdit.bankDetails,
        taxDetails: selectedPayoutForEdit.taxDetails,
        payslip: {
          documentName: selectedPayoutForEdit.payslip?.documentName || '',
          description: selectedPayoutForEdit.payslip?.description || '',
          verificationDate:
            selectedPayoutForEdit.payslip?.verificationDate || '',
          status:
            selectedPayoutForEdit.payslip?.status || TDocumentStatus.PENDING,
        },
        remarks: selectedPayoutForEdit.remarks || '',
      });
    } else if (isModalOpen) {
      reset(defaultValues);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedPayoutForEdit, isModalOpen, reset]);

  const onSubmitForm = (data: StaffPayoutFormData) => {
    const selectedStaff = staffListResponse?.data.find(
      (s) => s.id === data.userId
    );
    if (!selectedStaff) return;

    const user: TPrimaryDetails = {
      id: selectedStaff.id,
      firstName: selectedStaff.basicDetails.firstName,
      middleName: selectedStaff.basicDetails.middleName || null,
      lastName: selectedStaff.basicDetails.lastName,
      gender: selectedStaff.basicDetails.gender,
    };

    const payslipDocument: TDocument = {
      documentId: selectedPayoutForEdit?.payslip?.documentId || '',
      userId: data.userId,
      documentType: 'SALARY_SLIP' as any,
      documentName: data.payslip.documentName,
      submissionDate:
        selectedPayoutForEdit?.payslip?.submissionDate ||
        new Date().toISOString().split('T')[0],
      verificationDate: data.payslip.verificationDate || undefined,
      status: data.payslip.status,
      remarks: data.remarks || undefined,
      fileUrl: selectedPayoutForEdit?.payslip?.fileUrl || undefined,
      thumbnailUrl: selectedPayoutForEdit?.payslip?.thumbnailUrl || undefined,
      description: data.payslip.description || undefined,
      uploadedAt: selectedPayoutForEdit?.payslip?.uploadedAt || {
        userId: data.userId,
        timestamp: Date.now(),
      },
    };

    const payoutData: Partial<IStaffPayout> = {
      payoutId: selectedPayoutForEdit?.payoutId || '',
      user,
      salaryBreakdown: data.salaryBreakdown,
      amount: data.salaryBreakdown.netSalary,
      month: data.month,
      year: data.year,
      financialYear: data.financialYear,
      payPeriod: data.payPeriod,
      workingDays: data.workingDays,
      paidDays: data.paidDays,
      status: data.status,
      paymentMethod: data.paymentMethod,
      paymentDate: data.paymentDate,
      transactionReference: data.transactionReference,
      bankDetails: data.bankDetails,
      taxDetails: data.taxDetails,
      payslip: payslipDocument,
      remarks: data.remarks,
    };

    // TODO: Call API to save payout
    console.log('Payout data:', payoutData);
    closeModal();
  };

  const currentYear = new Date().getFullYear();
  const yearOptions: TKeyValue<string>[] = Array.from(
    { length: 10 },
    (_, i) => {
      const year = currentYear - 5 + i;
      return { key: year.toString(), value: year.toString() };
    }
  );

  return (
    <StructuredModal
      isOpen={isModalOpen}
      onClose={closeModal}
      title={
        selectedPayoutForEdit
          ? translateText('staff.payouts.editPayout') || 'Edit Payout'
          : translateText('staff.payouts.addPayout') || 'Add Payout'
      }
      drawerWidth="90vw"
      footerPositiveAction={handleSubmit(onSubmitForm)}
      footerPositiveActionText={translateText('common.save')}
      footerNegativeAction={closeModal}
      footerNegativeActionText={translateText('common.cancel')}
      isLoading={payslipUploadProgress}
      footerMessage={
        mutationErrorMessage ? (
          <Typography color="error" variant="body2">
            {mutationErrorMessage}
          </Typography>
        ) : null
      }
    >
      <FormProvider {...methods}>
        <form onSubmit={handleSubmit(onSubmitForm)} style={{ width: '100%' }}>
          <Grid container spacing={{ xs: 1, sm: 2, md: 3 }} sx={{ p: { xs: 1, sm: 2 } }}>
            {/* Basic Information */}
            <Grid item xs={12}>
              <Typography
                variant="h6"
                gutterBottom
                sx={{
                  fontSize: { xs: '1.125rem', sm: '1.25rem', md: '1.5rem' },
                }}
              >
                {translateText('staff.payouts.basicInformation') ||
                  'Basic Information'}
              </Typography>
            </Grid>

            <Grid item xs={12} md={6}>
              <ControlledSelectField
                control={control}
                name="userId"
                id="staff-user"
                label={
                  translateText('staff.payouts.staffMember') || 'Staff Member'
                }
                itemList={staffOptions}
                errorMessage={errors.userId?.message}
                isRequired
                isGridItem={false}
              />
            </Grid>

            <Grid item xs={12} sm={6} md={3}>
              <ControlledSelectField
                control={control}
                name="month"
                id="payout-month"
                label={translateText('staff.payouts.month') || 'Month'}
                itemList={getMonthOptions()}
                errorMessage={errors.month?.message}
                isRequired
                isGridItem={false}
              />
            </Grid>

            <Grid item xs={12} sm={6} md={3}>
              <ControlledSelectField
                control={control}
                name="year"
                id="payout-year"
                label={translateText('staff.payouts.year') || 'Year'}
                itemList={yearOptions}
                errorMessage={errors.year?.message}
                isRequired
                isGridItem={false}
              />
            </Grid>

            <Grid item xs={12} sm={6} md={4}>
              <ControlledTextField
                control={control}
                name="financialYear"
                id="financial-year"
                label={
                  translateText('staff.payouts.financialYear') ||
                  'Financial Year'
                }
                errorMessage={errors.financialYear?.message}
                isGridItem={false}
                required
              />
            </Grid>

            <Grid item xs={12} sm={6} md={4}>
              <ControlledSelectField
                control={control}
                name="payPeriod"
                id="pay-period"
                label={translateText('staff.payouts.payPeriod') || 'Pay Period'}
                itemList={getPayPeriodOptions(translateText)}
                errorMessage={errors.payPeriod?.message}
                isRequired
                isGridItem={false}
              />
            </Grid>

            <Grid item xs={12} sm={6} md={4}>
              <ControlledSelectField
                control={control}
                name="status"
                id="payout-status"
                label={translateText('staff.payouts.status') || 'Status'}
                itemList={getStatusOptions(translateText)}
                errorMessage={errors.status?.message}
                isRequired
                isGridItem={false}
              />
            </Grid>

            <Grid item xs={12} sm={6} md={6}>
              <ControlledTextField
                control={control}
                name="workingDays"
                id="working-days"
                label={
                  translateText('staff.payouts.workingDays') || 'Working Days'
                }
                errorMessage={errors.workingDays?.message}
                isGridItem={false}
                type="number"
              />
            </Grid>

            <Grid item xs={12} sm={6} md={6}>
              <ControlledTextField
                control={control}
                name="paidDays"
                id="paid-days"
                label={translateText('staff.payouts.paidDays') || 'Paid Days'}
                errorMessage={errors.paidDays?.message}
                isGridItem={false}
                type="number"
              />
            </Grid>

            <Grid item xs={12}>
              <Divider sx={{ my: 2 }} />
            </Grid>

            {/* Salary Breakdown - Earnings */}
            <Grid item xs={12}>
              <Typography
                variant="h6"
                gutterBottom
                sx={{
                  fontSize: { xs: '1.125rem', sm: '1.25rem', md: '1.5rem' },
                }}
              >
                {translateText('staff.payouts.salaryBreakdown') ||
                  'Salary Breakdown'}
              </Typography>
            </Grid>

            <Grid item xs={12}>
              <Typography
                variant="subtitle2"
                gutterBottom
                sx={{
                  fontSize: { xs: '0.875rem', sm: '0.875rem', md: '0.875rem' },
                }}
              >
                {translateText('staff.payouts.earnings') || 'Earnings'}
              </Typography>
            </Grid>

            <Grid item xs={12} sm={6} md={4}>
              <ControlledTextField
                control={control}
                name="salaryBreakdown.basic"
                id="basic-salary"
                label={translateText('staff.payouts.basic') || 'Basic Salary'}
                errorMessage={errors.salaryBreakdown?.basic?.message}
                isGridItem={false}
                type="number"
                required
              />
            </Grid>

            <Grid item xs={12} sm={6} md={4}>
              <ControlledTextField
                control={control}
                name="salaryBreakdown.hra"
                id="hra"
                label={translateText('staff.payouts.hra') || 'HRA'}
                errorMessage={errors.salaryBreakdown?.hra?.message}
                isGridItem={false}
                type="number"
              />
            </Grid>

            <Grid item xs={12} sm={6} md={4}>
              <ControlledTextField
                control={control}
                name="salaryBreakdown.da"
                id="da"
                label={translateText('staff.payouts.da') || 'DA'}
                errorMessage={errors.salaryBreakdown?.da?.message}
                isGridItem={false}
                type="number"
              />
            </Grid>

            <Grid item xs={12} sm={6} md={4}>
              <ControlledTextField
                control={control}
                name="salaryBreakdown.transportAllowance"
                id="transport-allowance"
                label={
                  translateText('staff.payouts.transportAllowance') ||
                  'Transport Allowance'
                }
                errorMessage={
                  errors.salaryBreakdown?.transportAllowance?.message
                }
                isGridItem={false}
                type="number"
              />
            </Grid>

            <Grid item xs={12} sm={6} md={4}>
              <ControlledTextField
                control={control}
                name="salaryBreakdown.medicalAllowance"
                id="medical-allowance"
                label={
                  translateText('staff.payouts.medicalAllowance') ||
                  'Medical Allowance'
                }
                errorMessage={errors.salaryBreakdown?.medicalAllowance?.message}
                isGridItem={false}
                type="number"
              />
            </Grid>

            <Grid item xs={12} sm={6} md={4}>
              <ControlledTextField
                control={control}
                name="salaryBreakdown.specialAllowance"
                id="special-allowance"
                label={
                  translateText('staff.payouts.specialAllowance') ||
                  'Special Allowance'
                }
                errorMessage={errors.salaryBreakdown?.specialAllowance?.message}
                isGridItem={false}
                type="number"
              />
            </Grid>

            <Grid item xs={12} sm={6} md={4}>
              <ControlledTextField
                control={control}
                name="salaryBreakdown.otherAllowances"
                id="other-allowances"
                label={
                  translateText('staff.payouts.otherAllowances') ||
                  'Other Allowances'
                }
                errorMessage={errors.salaryBreakdown?.otherAllowances?.message}
                isGridItem={false}
                type="number"
              />
            </Grid>

            <Grid item xs={12} sm={6} md={4}>
              <ControlledTextField
                control={control}
                name="salaryBreakdown.grossSalary"
                id="gross-salary"
                label={
                  translateText('staff.payouts.grossSalary') || 'Gross Salary'
                }
                errorMessage={errors.salaryBreakdown?.grossSalary?.message}
                isGridItem={false}
                type="number"
                disabled
              />
            </Grid>

            <Grid item xs={12}>
              <Divider sx={{ my: 2 }} />
            </Grid>

            {/* Deductions */}
            <Grid item xs={12}>
              <Typography
                variant="subtitle2"
                gutterBottom
                sx={{
                  fontSize: { xs: '0.875rem', sm: '0.875rem', md: '0.875rem' },
                }}
              >
                {translateText('staff.payouts.deductions') || 'Deductions'}
              </Typography>
            </Grid>

            <Grid item xs={12} sm={6} md={4}>
              <ControlledTextField
                control={control}
                name="salaryBreakdown.providentFund"
                id="provident-fund"
                label={
                  translateText('staff.payouts.providentFund') ||
                  'Provident Fund (PF)'
                }
                errorMessage={errors.salaryBreakdown?.providentFund?.message}
                isGridItem={false}
                type="number"
              />
            </Grid>

            <Grid item xs={12} sm={6} md={4}>
              <ControlledTextField
                control={control}
                name="salaryBreakdown.employeeStateInsurance"
                id="esi"
                label={translateText('staff.payouts.esi') || 'ESI'}
                errorMessage={
                  errors.salaryBreakdown?.employeeStateInsurance?.message
                }
                isGridItem={false}
                type="number"
              />
            </Grid>

            <Grid item xs={12} sm={6} md={4}>
              <ControlledTextField
                control={control}
                name="salaryBreakdown.professionalTax"
                id="professional-tax"
                label={
                  translateText('staff.payouts.professionalTax') ||
                  'Professional Tax'
                }
                errorMessage={errors.salaryBreakdown?.professionalTax?.message}
                isGridItem={false}
                type="number"
              />
            </Grid>

            <Grid item xs={12} sm={6} md={4}>
              <ControlledTextField
                control={control}
                name="salaryBreakdown.incomeTax"
                id="income-tax"
                label={
                  translateText('staff.payouts.incomeTax') || 'Income Tax (TDS)'
                }
                errorMessage={errors.salaryBreakdown?.incomeTax?.message}
                isGridItem={false}
                type="number"
              />
            </Grid>

            <Grid item xs={12} sm={6} md={4}>
              <ControlledTextField
                control={control}
                name="salaryBreakdown.otherDeductions"
                id="other-deductions"
                label={
                  translateText('staff.payouts.otherDeductions') ||
                  'Other Deductions'
                }
                errorMessage={errors.salaryBreakdown?.otherDeductions?.message}
                isGridItem={false}
                type="number"
              />
            </Grid>

            <Grid item xs={12} sm={6} md={4}>
              <ControlledTextField
                control={control}
                name="salaryBreakdown.totalDeductions"
                id="total-deductions"
                label={
                  translateText('staff.payouts.totalDeductions') ||
                  'Total Deductions'
                }
                errorMessage={errors.salaryBreakdown?.totalDeductions?.message}
                isGridItem={false}
                type="number"
                disabled
              />
            </Grid>

            <Grid item xs={12} sm={6} md={4}>
              <ControlledTextField
                control={control}
                name="salaryBreakdown.netSalary"
                id="net-salary"
                label={translateText('staff.payouts.netSalary') || 'Net Salary'}
                errorMessage={errors.salaryBreakdown?.netSalary?.message}
                isGridItem={false}
                type="number"
                disabled
              />
            </Grid>

            <Grid item xs={12}>
              <Divider sx={{ my: 2 }} />
            </Grid>

            {/* Payment Details */}
            <Grid item xs={12}>
              <Typography
                variant="h6"
                gutterBottom
                sx={{
                  fontSize: { xs: '1.125rem', sm: '1.25rem', md: '1.5rem' },
                }}
              >
                {translateText('staff.payouts.paymentDetails') ||
                  'Payment Details'}
              </Typography>
            </Grid>

            <Grid item xs={12} sm={6} md={4}>
              <ControlledSelectField
                control={control}
                name="paymentMethod"
                id="payment-method"
                label={
                  translateText('staff.payouts.paymentMethod') ||
                  'Payment Method'
                }
                itemList={getPaymentMethodOptions(translateText)}
                errorMessage={errors.paymentMethod?.message}
                isGridItem={false}
              />
            </Grid>

            <Grid item xs={12} sm={6} md={4}>
              <ControlledDateField
                control={control}
                name="paymentDate"
                id="payment-date"
                label={
                  translateText('staff.payouts.paymentDate') || 'Payment Date'
                }
                errorMessage={errors.paymentDate?.message}
                isGridItem={false}
              />
            </Grid>

            <Grid item xs={12} sm={6} md={4}>
              <ControlledTextField
                control={control}
                name="transactionReference"
                id="transaction-reference"
                label={
                  translateText('staff.payouts.transactionReference') ||
                  'Transaction Reference'
                }
                errorMessage={errors.transactionReference?.message}
                isGridItem={false}
              />
            </Grid>

            {/* Bank Details */}
            <Grid item xs={12}>
              <Typography
                variant="subtitle2"
                gutterBottom
                sx={{
                  mt: { xs: 1, sm: 2 },
                  fontSize: { xs: '0.875rem', sm: '0.875rem', md: '0.875rem' },
                }}
              >
                {translateText('staff.payouts.bankDetails') || 'Bank Details'}
              </Typography>
            </Grid>

            <Grid item xs={12} sm={6} md={6}>
              <ControlledTextField
                control={control}
                name="bankDetails.accountNumber"
                id="account-number"
                label={
                  translateText('staff.payouts.accountNumber') ||
                  'Account Number'
                }
                errorMessage={errors.bankDetails?.accountNumber?.message}
                isGridItem={false}
              />
            </Grid>

            <Grid item xs={12} sm={6} md={6}>
              <ControlledTextField
                control={control}
                name="bankDetails.ifscCode"
                id="ifsc-code"
                label={translateText('staff.payouts.ifscCode') || 'IFSC Code'}
                errorMessage={errors.bankDetails?.ifscCode?.message}
                isGridItem={false}
              />
            </Grid>

            <Grid item xs={12} sm={6} md={6}>
              <ControlledTextField
                control={control}
                name="bankDetails.bankName"
                id="bank-name"
                label={translateText('staff.payouts.bankName') || 'Bank Name'}
                errorMessage={errors.bankDetails?.bankName?.message}
                isGridItem={false}
              />
            </Grid>

            <Grid item xs={12} sm={6} md={6}>
              <ControlledTextField
                control={control}
                name="bankDetails.branchName"
                id="branch-name"
                label={
                  translateText('staff.payouts.branchName') || 'Branch Name'
                }
                errorMessage={errors.bankDetails?.branchName?.message}
                isGridItem={false}
              />
            </Grid>

            <Grid item xs={12}>
              <Divider sx={{ my: 2 }} />
            </Grid>

            {/* Tax Details */}
            <Grid item xs={12}>
              <Typography
                variant="h6"
                gutterBottom
                sx={{
                  fontSize: { xs: '1.125rem', sm: '1.25rem', md: '1.5rem' },
                }}
              >
                {translateText('staff.payouts.taxDetails') || 'Tax Details'}
              </Typography>
            </Grid>

            <Grid item xs={12} sm={6} md={6}>
              <ControlledTextField
                control={control}
                name="taxDetails.pan"
                id="pan"
                label={translateText('staff.payouts.pan') || 'PAN'}
                errorMessage={errors.taxDetails?.pan?.message}
                isGridItem={false}
              />
            </Grid>

            <Grid item xs={12} sm={6} md={6}>
              <ControlledTextField
                control={control}
                name="taxDetails.uan"
                id="uan"
                label={translateText('staff.payouts.uan') || 'UAN'}
                errorMessage={errors.taxDetails?.uan?.message}
                isGridItem={false}
              />
            </Grid>

            <Grid item xs={12} sm={6} md={6}>
              <ControlledTextField
                control={control}
                name="taxDetails.tdsAmount"
                id="tds-amount"
                label={translateText('staff.payouts.tdsAmount') || 'TDS Amount'}
                errorMessage={errors.taxDetails?.tdsAmount?.message}
                isGridItem={false}
                type="number"
              />
            </Grid>

            <Grid item xs={12} sm={6} md={6}>
              <ControlledTextField
                control={control}
                name="taxDetails.taxSlab"
                id="tax-slab"
                label={translateText('staff.payouts.taxSlab') || 'Tax Slab'}
                errorMessage={errors.taxDetails?.taxSlab?.message}
                isGridItem={false}
              />
            </Grid>

            <Grid item xs={12}>
              <Divider sx={{ my: 2 }} />
            </Grid>

            {/* Payslip Document */}
            <Grid item xs={12}>
              <Typography
                variant="h6"
                gutterBottom
                sx={{
                  fontSize: { xs: '1.125rem', sm: '1.25rem', md: '1.5rem' },
                }}
              >
                {translateText('staff.payouts.payslip') || 'Payslip'}
              </Typography>
            </Grid>

            <Grid item xs={12}>
              <ControlledFileUploadField
                control={control}
                name="payslip.documentName"
                id="payslip-upload"
                label={translateText('common.selectFile') || 'Select File'}
                sectionTitle={
                  translateText('staff.payouts.payslip') || 'Payslip'
                }
                isRequired
                errorMessage={
                  errors.payslip?.documentName?.message ||
                  payslipUploadProgressMessage
                }
                descriptionError={errors.payslip?.description?.message}
                verificationError={errors.payslip?.verificationDate?.message}
                statusError={errors.payslip?.status?.message}
                onUploadProgress={onPayslipUploadProgress}
              />
            </Grid>

            <Grid item xs={12} sm={6} md={6}>
              <ControlledSelectField
                control={control}
                name="payslip.status"
                id="payslip-status"
                label={translateText('common.status') || 'Status'}
                itemList={getStatusOptionsForDocument(translateText)}
                errorMessage={errors.payslip?.status?.message}
                isRequired
                isGridItem={false}
              />
            </Grid>

            <Grid item xs={12}>
              <Divider sx={{ my: 2 }} />
            </Grid>

            {/* Remarks */}
            <Grid item xs={12}>
              <ControlledTextField
                control={control}
                name="remarks"
                id="remarks"
                label={translateText('common.remarks') || 'Remarks'}
                errorMessage={errors.remarks?.message}
                isGridItem={false}
                multiline
                rows={3}
                fullWidth
              />
            </Grid>
          </Grid>
        </form>
      </FormProvider>
    </StructuredModal>
  );
};

export default AddEditStaffPayoutModal;
